import {PIXI} from '@colonist/pixi'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {UIGameManager} from '../../ui_game/UIGameManager'
import {UIGameTradePlayerArrowInfoView} from '../UIGameTradePlayerArrowInfoView'
import {CardView} from '../../ui_canvas_engine/CardView'
import {UIGameConstants} from '../../ui_game/UIGameConstants'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {getMerchantImageNameForPlayer, Sprites} from '../../ui_game/UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {CardEnum, MechanicMerchantState, PlayerColor} from '@colonist/game-states'
import {TradeUtils} from '@colonist/game-mechanic-validators'

export class UIGameTradeProposalView extends SectionBgView {
    private readonly uiGameManager: UIGameManager
    private merchantIcon: SpriteView | undefined
    private merchantCardView: CardView | undefined

    tradingTargetColors: PlayerColor

    readonly wantedCardPlayerInfo: UIGameTradePlayerArrowInfoView
    readonly offeredCardPlayerInfo: UIGameTradePlayerArrowInfoView

    constructor(uiGameManager: UIGameManager, x: number, y: number, w: number, h: number, container: PIXI.Container, onCardCountChangeAction: () => void, targetColor: PlayerColor = PlayerColor.None) {
        super(x, y, w, h, container)

        this.uiGameManager = uiGameManager
        this.drawRoundedBg()
        const margin = uiGameManager.canvasController.margin
        const iconScalarOverride = 0.5

        const informationHeight = h / 2
        this.tradingTargetColors = targetColor
        const tradingTargetArrowTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trade_arrow_green)
        const partitionEnabled = true
        this.wantedCardPlayerInfo = new UIGameTradePlayerArrowInfoView(uiGameManager, 0, 0, this.width, informationHeight, targetColor, tradingTargetArrowTexture, [], this, false, partitionEnabled, iconScalarOverride)
        this.wantedCardPlayerInfo.justifyAvatarsToTopLeft()

        const offeredCardPlayerInfoY = informationHeight - margin * 4
        const myColor = uiGameManager.gameController.myColor
        const myArrowTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trade_arrow_red)
        this.offeredCardPlayerInfo = new UIGameTradePlayerArrowInfoView(uiGameManager, 0, offeredCardPlayerInfoY, this.width, informationHeight, myColor, myArrowTexture, [], this, false, partitionEnabled, iconScalarOverride)
        this.offeredCardPlayerInfo.justifyAvatarsToBottomLeft()

        this.wantedCardPlayerInfo.cardContainer.setCardClickAction((cardEnum: CardEnum) => {
            this.wantedCardPlayerInfo.cardContainer.removeCard(cardEnum)
        })

        this.offeredCardPlayerInfo.cardContainer.setCardClickAction((cardEnum: CardEnum) => {
            if(this.uiGameManager.tradeController.inBankTradeMode) {
                this.onCardClickBankTrade(cardEnum)
                return
            }
            this.onCardClickPlayersTrade(cardEnum)
        })

        this.offeredCardPlayerInfo.cardContainer.setCardRightClickAction((cardEnum: CardEnum) => {
            this.onCardClickBankTrade(cardEnum)
        })

        this.wantedCardPlayerInfo.cardContainer.setCardAddAction(onCardCountChangeAction)
        this.wantedCardPlayerInfo.cardContainer.setCardRemoveAction(onCardCountChangeAction)
        this.offeredCardPlayerInfo.cardContainer.setCardAddAction(onCardCountChangeAction)
        this.offeredCardPlayerInfo.cardContainer.setCardRemoveAction(onCardCountChangeAction)

        // block cursor events for objects behind this view
        this.interactive = true
    }

    removeAllCards() {
        this.wantedCardPlayerInfo.cardContainer.removeAllCards()
        this.offeredCardPlayerInfo.cardContainer.removeAllCards()
    }

    private onCardClickPlayersTrade(cardEnum: CardEnum) {
        this.offeredCardPlayerInfo.cardContainer.removeCard(cardEnum)
        if(cardEnum == CardEnum.AnyResource) return
        const cardView = this.uiGameManager.tradeController.offeredCardSelectorView.cardsContainer.addNewCardOfType(cardEnum, false)
        this.uiGameManager.tradeController.offeredCardSelectorView.beginnerCardAnimationController.addCardToBeginnerCardAnimations(cardView)
    }

    onPlayerControllerState() {
        void this.wantedCardPlayerInfo.refreshAvatarLeftIcon()
        void this.offeredCardPlayerInfo.refreshAvatarLeftIcon()
    }

    private onCardClickBankTrade(cardEnum: CardEnum) {
        if(cardEnum == CardEnum.AnyResource) return this.onCardClickPlayersTrade(cardEnum)

        const bankTradeRatios = this.uiGameManager.gameController.getPlayerBankTradeRatios(this.uiGameManager.gameController.myColor)
        const tradeRatio = TradeUtils.getBankTradeRatioForCard(bankTradeRatios, cardEnum)
        const cardCount = this.offeredCardPlayerInfo.cardContainer.cardInventory.getCardTypeCount(cardEnum)

        let cardsToRemove = cardCount % tradeRatio
        cardsToRemove = cardsToRemove === 0 ? tradeRatio : cardsToRemove

        for(let i = 0; i < cardsToRemove; i++) {
            this.offeredCardPlayerInfo.cardContainer.removeCard(cardEnum)
            this.uiGameManager.tradeController.offeredCardSelectorView.cardsContainer.addNewCardOfType(cardEnum)
        }
    }

    addMerchantToCardView(cardEnum: CardEnum) {
        if(this.merchantIcon !== undefined) this.merchantIcon.destroy()
        if(this.merchantCardView !== undefined) this.merchantCardView.destroy()

        this.addCardView(cardEnum)
        this.addMerchant()
    }

    updateMerchantView(merchantState: MechanicMerchantState) {
        if(this.uiGameManager.gameController.myColor == merchantState.owner) return
        if(this.merchantIcon != undefined) this.merchantIcon.parent.removeChild(this.merchantIcon)
        if(this.merchantCardView != undefined) this.merchantCardView.parent.removeChild(this.merchantCardView)

        this.merchantCardView = undefined
        this.merchantIcon = undefined
    }

    private addCardView(cardEnum: CardEnum) {
        const cardViewToProposalViewRatio = 0.38
        const cardHeight = this.height * cardViewToProposalViewRatio
        const cardWidth = cardHeight / UIGameConstants.CARD_RATIO
        const paddingX = this.uiGameManager.canvasController.margin * 4
        const paddingY = this.uiGameManager.canvasController.margin * 5

        const cardX = this.width - paddingX - cardWidth
        const cardY = this.height - paddingY - cardHeight
        this.merchantCardView = new CardView(this.uiGameManager, 0, cardX, cardY, cardWidth, cardHeight, cardEnum, this)
        this.merchantCardView.hideBadge()
    }

    private addMerchant() {
        const cardView = this.merchantCardView
        if(cardView == undefined) return

        const paddingY = this.uiGameManager.canvasController.margin * 5

        const merchantTextureImageName = getMerchantImageNameForPlayer(this.uiGameManager.gameController.myColor)
        const merchantTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites[merchantTextureImageName])
        const ratioOfMerchantToCardView = 0.55
        const merchantIconSize = cardView.width * ratioOfMerchantToCardView
        const merchantIconX = cardView.x + cardView.width - merchantIconSize * 0.7
        const merchantIconY = this.height - merchantIconSize * 0.75 - paddingY
        this.merchantIcon = new SpriteView(merchantIconX, merchantIconY, merchantIconSize, merchantIconSize, this, merchantTexture)
    }
}
